<script context="module" lang="ts">
	export function slugToColor(
		forSlug: string,
		menuItems: any[],
		ret: "hex" | "rgba" = "hex"
	) {
		try {
			if (!forSlug || !menuItems || menuItems.length === 0) {
				return "#ED2724";
			}

			const slug = forSlug.indexOf("/") === 0 ? forSlug : `/${forSlug}`;

			let color = "#ED2724";

			for (const item of menuItems) {
				if (!item) {
					continue;
				}

				if (slug.indexOf(item.url) === 0) {
					color = item.color;

					if (item.childs) {
						for (const child of item.childs) {
							if (!child) {
								continue;
							}

							if (slug.indexOf(child.url) === 0 && child.color) {
								color = child.color;
								break;
							}
						}
					}

					break;
				}
			}

			return ret === "hex" ? color : convertHexToRGBA(color);
		} catch (e) {
			console.error(e, menuItems);
		}
	}

	export const convertHexToRGBA = (
		hexCode: string = "#ED2724",
		opacity: number = 1
	) => {
		let hex = (hexCode || "#ED2724").replace("#", "");

		if (hex.length === 3) {
			hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
		}

		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);

		if (opacity > 1 && opacity <= 100) {
			opacity = opacity / 100;
		}

		return `rgba(${r},${g},${b},${opacity})`;
	};

	export function hasSubmenu(path: string, menuItems: any[]): any {
		if (!menuItems || menuItems.length === 0) {
			return false;
		}

		for (const item of menuItems) {
			if (path.indexOf(item.url) === 0) {
				return item.childs;
			}
		}
	}

	export function sortItems(items: any[]): any[] {
		if (!items || items.length === 0) {
			return [];
		}

		const ret = [...items].sort(
			(a: any, b: any) => b.url?.length - a.url?.length
		);

		return ret.map((item: any) => {
			if (item.childs) {
				item.childs = item.childs.sort(
					(a: any, b: any) => b.url?.length - a.url?.length
				);
			}
			return item;
		});
	}
</script>

<script lang="ts">
	import { onMount, getContext } from "svelte";
	import Hydrator from "@reperio-news/core.components/Hydrator.svelte";

	const pageContext: SvelteStore<any> = getContext("context");
	import { slide, fly, fade } from "svelte/transition";
	// @ts-ignore
	import logo from "./logo.png";
	// @ts-ignore
	import searchSvg from "./search.svg";
	// @ts-ignore
	import menuIcon from "./menuIcon.svg";
	// @ts-ignore
	import closeIcon from "./closeIcon.svg";
	// @ts-ignore
	import chevronLeft from "./chevron-left.svg";
	// @ts-ignore
	import chevronRight from "./chevron-right.svg";
	// @ts-ignore
	import chevronDown from "./chevron-down.svg";

	export let id: string;
	export let globalId: string;
	export let globalSource: number;
	// ignore no use
	id || globalId || globalSource;

	export let data = {
		items: [],
	};

	export let context: any;

	let showMenu: boolean = false;
	let scrollY: number;
	let showShareContent = false;
	let hasScrolledOnce = false;
	let containerDom: HTMLDivElement;

	function onScroll(_scrollY: number, _sm?: boolean) {
		if (isMobile && scrollY > 100) {
			hasScrolledOnce = true;
		}
		showShareContent =
			(hasScrolledOnce || scrollY > 100) && context && context.type === "content";

		if (containerDom && !__ADMIN__) {
			let top = 0;

			if (showMenu) {
				top = 0;
			} else {
				top = Math.max(fakeDom.getBoundingClientRect().top, 0);
			}
			containerDom.style.top = `${top}px`;
		}
	}

	$: onScroll(scrollY, showMenu);

	//	viewport-based anchoring under the menu button; state + style:
	//	directives, never imperative menuDom.style writes (offsetLeft was
	//	relative to the centered .subcontainer, which put the fixed-position
	//	menu mid-screen on desktop)
	let menuTop = 57;
	let menuRight = 10;

	function positionMenu(open: boolean, _dom: any) {
		//	containerDom re-checked at run time — null mid-flight during the
		//	Hydrator branch swap; _dom in the deps re-runs this after re-bind
		if (!open || !containerDom) {
			return;
		}

		const rect = containerDom.getBoundingClientRect();
		menuTop = Math.max(rect.bottom, rect.height);

		const menuButton: HTMLDivElement | null =
			containerDom.querySelector(".menuButton");

		if (menuButton) {
			menuRight = Math.max(
				window.innerWidth - menuButton.getBoundingClientRect().right,
				0
			);
		}
	}

	$: positionMenu(showMenu, containerDom);

	$: if (mounted && !__ADMIN__) {
		document.body.style.overflow = showMenu ? "hidden" : "";
	}

	let color = "#ED2724";
	export let subMenuItems: any;

	let sortedItems: any;

	$: if (data && data.items && context) {
		sortedItems = sortItems(data.items);

		if (context.path !== "/") {
			color = slugToColor(context.path, sortedItems);
			subMenuItems = hasSubmenu(context.path, sortedItems);
		}
	}

	const keyDown = (event: any) => {
		if (event.key === "Escape") {
			showMenu = false;
			showSearch = false;
		}
	};

	let mainScroll: any;
	let subMenuScroll: any;
	let mainScrollCurrentLeft: number = 0;
	let subMenuScrollCurrentLeft: number = 0;
	let mainScrollMax = 0;
	let subMenuScrollMax = 0;
	const scrollInc = 75;
	let fakeDom: HTMLDivElement = null;

	const scrollLeft = (obj: "main" | "submenu") => {
		if (obj === "main" && mainScroll) {
			mainScrollCurrentLeft = mainScroll.scrollLeft - scrollInc;

			mainScroll.scroll({
				top: 0,
				left: mainScrollCurrentLeft,
				behavior: "smooth",
			});
		} else if (subMenuScroll) {
			subMenuScrollCurrentLeft = subMenuScroll.scrollLeft - scrollInc;

			subMenuScroll.scroll({
				top: 0,
				left: subMenuScrollCurrentLeft,
				behavior: "smooth",
			});
		}
	};

	const scrollRight = (obj: "main" | "submenu") => {
		if (obj === "main" && mainScroll) {
			mainScrollCurrentLeft = mainScroll.scrollLeft + scrollInc;

			mainScroll.scroll({
				top: 0,
				left: mainScrollCurrentLeft,
				behavior: "smooth",
			});
		} else if (subMenuScroll) {
			subMenuScrollCurrentLeft = subMenuScroll.scrollLeft + scrollInc;

			subMenuScroll.scroll({
				top: 0,
				left: subMenuScrollCurrentLeft,
				behavior: "smooth",
			});
		}
	};

	let isMobile = $pageContext?.client?.is_mobile || false;
	let mounted = false;

	const setMaxScroll = () => {
		if (mainScroll) {
			mainScrollMax =
				[...mainScroll.querySelectorAll("a")].reduce(
					(acc: any, el: any) => {
						return acc + el.getBoundingClientRect().width;
					},
					0
				) - mainScroll.getBoundingClientRect().width;
		}

		if (subMenuScroll) {
			subMenuScrollMax =
				[...subMenuScroll.querySelectorAll("a")].reduce(
					(acc: any, el: any) => {
						return acc + el.getBoundingClientRect().width;
					},
					0
				) - subMenuScroll.getBoundingClientRect().width;
		}
	};

	const scrollActiveIntoView = (scroller: HTMLElement | null): number => {
		if (!scroller) {
			return 0;
		}

		const active = scroller.querySelector(".selected") as HTMLElement | null;
		if (!active) {
			return scroller.scrollLeft;
		}

		const sRect = scroller.getBoundingClientRect();
		const aRect = active.getBoundingClientRect();

		if (aRect.left >= sRect.left && aRect.right <= sRect.right) {
			return scroller.scrollLeft;
		}

		//	center the active item; rect-based so it's independent of
		//	offsetParent quirks. "instant" because the container's CSS
		//	scroll-behavior:smooth would otherwise animate the initial position
		const delta = aRect.left + aRect.width / 2 - (sRect.left + sRect.width / 2);
		scroller.scrollTo({
			left: scroller.scrollLeft + delta,
			behavior: "instant",
		});

		return scroller.scrollLeft;
	};

	onMount(() => {
		isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			);

		setTimeout(() => {
			//	doms re-checked at execution time — the Hydrator swap can
			//	unbind them during the 500ms wait
			setMaxScroll();
			mainScrollCurrentLeft = scrollActiveIntoView(mainScroll);
			subMenuScrollCurrentLeft = scrollActiveIntoView(subMenuScroll);
		}, 500);

		// switch to fixed positioning now that JS can manage the top value
		//	doms can be unbound mid-flight during the Hydrator branch swap;
		//	onScroll (keyed on scrollY) re-applies the top value
		if (!__ADMIN__ && containerDom && fakeDom) {
			containerDom.style.top = `${fakeDom.getBoundingClientRect().top}px`;
		}
		mounted = true;

		return () => {
			if (!__ADMIN__) {
				document.body.style.overflow = "";
			}
		};
	});

	function onResize() {
		setMaxScroll();
		positionMenu(showMenu, containerDom);
	}

	let showSearch = false;
	let searchForm: any;

	function searchClick() {
		if (showSearch) {
			searchForm.submit();
			return;
		}

		showSearch = true;
	}

	//	open state must live in Svelte state, not classList — the compiler
	//	prunes scoped selectors for classes it never sees in the template
	let openSubs: Record<number, boolean> = {};

	function toggleSub(index: number) {
		openSubs[index] = !openSubs[index];
	}

	$: if (!showMenu) {
		openSubs = {};
	}

	let shareTooltip = "";
	let shareTooltipTimeout: any;

	async function doShare() {
		const url = `https://${context.domain}${context.path}`;
		const title = context.title || "";

		if (navigator.share) {
			try {
				await navigator.share({ title, url });
			} catch (_e) {
				// user cancelled
			}
		} else {
			try {
				await navigator.clipboard.writeText(url);
				shareTooltip = "Link copiat!";
				clearTimeout(shareTooltipTimeout);
				shareTooltipTimeout = setTimeout(() => {
					shareTooltip = "";
				}, 2000);
			} catch (_e) {
				// fallback: do nothing
			}
		}
	}

	function isAdmin(element: any) {
		if (__ADMIN__) {
			element.style.position = "relative";
		}
	}
</script>

<Hydrator props={$$props} component="TopMenu" hydrate={!__ADMIN__}>
	<div class="fake" class:fakeHidden={!mounted} class:fakeWithSubmenu={!isMobile && subMenuItems && subMenuItems.length} bind:this={fakeDom} />

	<div
		class="topmenu"
		class:withSubmenu={!isMobile && subMenuItems && subMenuItems.length
			? true
			: false}
		use:isAdmin
		bind:this={containerDom}
		class:fixed={!__ADMIN__ && mounted}
	>
		<div class="subcontainer">
			<a
				class="logo"
				href="/"
				title="Click | Ziarul Click! îţi aduce cele mai tari exclusivităţi, vedete, actualitate, divertisment, sport"
				><img width="100" height="47" src={logo} alt="click.ro" /></a
			>

			{#if showShareContent && !showSearch}
				{#if context.category && context.category.name}
					<a class="category-tag" href="/{context.category.slug}" style="background-color: {color};">
						{context.category.name}
					</a>
				{/if}
				<div class="title">
					{context.title}
				</div>

				<div class="share-btn" on:click={doShare} title="Distribuie">
					<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
						<polyline points="16 6 12 2 8 6"/>
						<line x1="12" y1="2" x2="12" y2="15"/>
					</svg>
					<span class="share-label">Trimite articol</span>
					{#if shareTooltip}
						<span class="share-tooltip">{shareTooltip}</span>
					{/if}
				</div>
			{:else if data && data.items}
				{#if showSearch}
					<form
						bind:this={searchForm}
						class="big"
						method="get"
						action="/search"
					>
						<!-- warn-disable a11y-autofocus -->
						<input
							type="search"
							name="q"
							placeholder="caută în click.ro"
							autofocus={true}
						/>
					</form>
				{:else}
					<div
						class="scrollable-left button"
						class:disabled={mainScrollCurrentLeft <= 0}
						on:click={() => scrollLeft("main")}
					>
						<img
							src={chevronLeft}
							alt="scroll left"
							width="24"
							height="24"
						/>
					</div>

					<div class="scrollable" bind:this={mainScroll}>
						<div class="items">
							{#each data.items as item}
								{#if item}
									<a
										class:selected={context &&
											context.path !== "/" &&
											item.url !== "/" &&
											context.path.indexOf(item.url) ===
												0}
										href={item.url}
										style="--color: {item.color}"
										target={item.target || "_self"}
										>{item.name}</a
									>
								{/if}
							{/each}
						</div>
					</div>
					<div
						class="scrollable-right button"
						class:disabled={mainScrollCurrentLeft >= mainScrollMax}
						on:click={() => scrollRight("main")}
					>
						<img
							src={chevronRight}
							alt="scroll right"
							width="24"
							height="24"
						/>
					</div>
				{/if}
			{/if}

			<div class="buttons">
				<div class="button" on:click={searchClick}>
					<img src={searchSvg} width="24" height="24" alt="căutare" />
				</div>

				{#if showSearch}
					<div class="button" on:click={() => (showSearch = false)}>
						<img
							src={closeIcon}
							width="24"
							height="24"
							alt="close"
						/>
					</div>
				{:else}
					<div
						class="button menuButton"
						class:withShareContent={showShareContent}
						class:menuButtonActive={showMenu}
						role="button"
						tabindex="0"
						aria-expanded={showMenu}
						aria-label="Meniu"
						on:click={() => (showMenu = !showMenu)}
						on:keydown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								showMenu = !showMenu;
							}
						}}
					>
						<img
							width="24"
							height="24"
							src={showMenu ? closeIcon : menuIcon}
							alt="Meniu"
						/>
					</div>
				{/if}
			</div>
		</div>

		{#if subMenuItems && subMenuItems.length}
			<div class="submenu" style="--color: {color};">
				<div
					class="scrollable-left button"
					class:disabled={subMenuScrollCurrentLeft <= 0}
					on:click={() => scrollLeft("submenu")}
				>
					<img
						src={chevronLeft}
						alt="scroll left"
						width="24"
						height="24"
					/>
				</div>

				<div class="scrollable" bind:this={subMenuScroll}>
					<div class="items">
						{#each subMenuItems as item}
							{#if item}
								<a
									class="submenu-item"
									href={item.url}
									class:selected={context &&
										context.path !== "/" &&
										item.url !== "/" &&
										context.path.indexOf(item.url) === 0 &&
										(!context.path[
											context.path.indexOf(item.url) +
												item.url.length
										] ||
											(context &&
												context.category &&
												context.category.slug ===
													item.url.substr(1)))}
									>{item.name}</a
								>
							{/if}
						{/each}
					</div>
				</div>

				<div
					class="scrollable-right button"
					class:disabled={subMenuScrollCurrentLeft >=
						subMenuScrollMax}
					on:click={() => scrollRight("submenu")}
				>
					<img
						src={chevronRight}
						alt="scroll right"
						width="24"
						height="24"
					/>
				</div>
			</div>
		{/if}
	</div>

	{#if showMenu}
		<div
			class="menuShadow"
			transition:fade|local={{ duration: 150 }}
			on:click={() => (showMenu = false)}
		/>
		<div
			class="menu"
			style:top="{menuTop}px"
			style:right="{menuRight}px"
			style:max-height="calc(100vh - {menuTop + 16}px)"
			transition:fly|local={{ y: -12, duration: 150 }}
		>
			<form class="small" method="get" action="/search">
				<input type="search" name="q" placeholder="căutare" />
				<img
					src={searchSvg}
					alt="căutare continut"
					on:click={(e) => e.target.parentNode.submit()}
				/>
			</form>

			<hr />

			{#if data && data.items}
				{#each data.items as item, index}
					{#if item}
						<div class="item" class:childs-open={openSubs[index]}>
							<a
								href={item.url}
								target={item.target || "_self"}
								style={item.color
									? `--border-color: ${item.color}`
									: ""}><span>{item.name}</span></a
							>

							{#if item.childs && item.childs.length}
								<div
									class="button"
									role="button"
									tabindex="0"
									aria-expanded={!!openSubs[index]}
									aria-label="Deschide submeniul {item.name}"
									on:click={() => toggleSub(index)}
									on:keydown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											toggleSub(index);
										}
									}}
								>
									<img
										src={chevronDown}
										width="16"
										height="16"
										alt="down"
									/>
								</div>

								{#if openSubs[index]}
									<div
										class="childs"
										transition:slide|local={{
											duration: 100,
										}}
									>
										{#each item.childs as child}
											<a
												class="child"
												href={child.url}
												target={child.target || "_self"}
												style={item.color
													? `--border-color: ${item.color}`
													: ""}
												><span>{child.name}</span></a
											>
										{/each}
									</div>
								{/if}
							{/if}
						</div>
					{/if}
				{/each}
			{/if}
		</div>
	{/if}
</Hydrator>

<svelte:window bind:scrollY on:keydown={keyDown} on:resize={onResize} />

<svelte:head>
	<meta name="theme-color" content={color} />
</svelte:head>

<style lang="scss">
	.disabled {
		visibility: hidden;
	}

	.fixed {
		position: fixed !important;
	}

	.topmenu {
		font-family: "PT Sans Narrow", sans-serif;
		user-select: none;
		font-size: 1.15em;
		letter-spacing: 0.5px;
		font-weight: 700;
		position: relative;
		left: 0px;
		top: 0px;
		border-bottom: 1px solid rgba(0, 0, 0, 0.1);
		background-color: #fff;
		z-index: 4;
		width: 100%;
		overflow: hidden;

		.subcontainer {
			display: flex;
			justify-content: space-between;
			align-items: center;
			max-width: calc(1000px - 20px);
			margin: auto;
			position: relative;
			padding: 10px;
			height: 37px;
			overflow: hidden;
			gap: 6px;

			.category-tag {
				flex-shrink: 0;
				color: #fff;
				padding: 3px 10px;
				border-radius: 4px;
				font-size: 0.7em;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				margin-left: 10px;
			}

			.title {
				color: rgb(65, 62, 62);
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
				margin-left: 8px;
				margin-right: 10px;
				height: 25px;
			}

			.share-btn {
				cursor: pointer;
				display: flex;
				justify-content: center;
				align-items: center;
				padding: 8px 16px;
				background-color: #ED2724;
				color: #fff;
				border-radius: 8px;
				font-size: 0.85em;
				font-weight: 600;
				gap: 6px;
				position: relative;
				flex-shrink: 0;
				transition: background-color 0.2s ease, transform 0.15s ease;
				margin-right: 6px;
			}

			.share-btn:hover {
				background-color: #c41f1d;
			}

			.share-btn:active {
				transform: scale(0.96);
			}

			.share-label {
				display: none;
			}

			.share-tooltip {
				position: absolute;
				bottom: -32px;
				left: 50%;
				transform: translateX(-50%);
				background-color: #333;
				color: #fff;
				padding: 4px 10px;
				border-radius: 6px;
				font-size: 0.75em;
				white-space: nowrap;
				pointer-events: none;
			}

			.logo {
				display: flex;
				justify-content: center;
				align-items: center;

				img {
					width: 100px;
					height: 47px;
				}
			}

			.scrollable::-webkit-scrollbar {
				display: none;
			}

			.scrollable {
				background-color: #ebe7e7;
				width: 80%;
				overflow: hidden;
				height: auto;
				overflow: scroll;
				border-radius: 5px;
				position: relative;
				scrollbar-width: none;
				-ms-overflow-style: none;
				scroll-behavior: smooth;

				.items {
					width: max-content;
					display: flex;
					align-items: center;
					justify-content: flex-start;
					flex-wrap: nowrap;
					height: 100%;
					position: relative;

					a.selected {
						background-color: var(--color);
						color: #fff;
					}

					a {
						color: rgb(65, 62, 62);
						padding: 5px 10px 5px 10px;
						background-color: #ebe7e7;
						border-right: 1px solid #ccc;
						overflow: hidden;
						min-width: 50px;
					}

					a:hover {
						background-color: var(--color);
						color: #fff;
						transition: background-color 0.2s ease-in-out;
					}
				}
			}

			.buttons {
				display: flex;
				flex-direction: row;
				flex-wrap: nowrap;
				align-items: center;
				gap: 6px;
			}

			.button {
				padding: 8px;
				background-color: #f5f2f2;
				border-radius: 8px;
				display: flex;
				justify-content: center;
				align-items: center;
				cursor: pointer;
				transition: background-color 0.2s ease;
			}

			.button:hover {
				background-color: rgb(241, 217, 217);
			}

			form {
				display: flex;
				justify-content: center;
				align-items: center;
				width: 80%;
				input {
					text-align: center;
					width: 100%;
					border: 1px solid #ccc;
					font-size: 1.25em;
					outline: none;
					height: 45px;
				}

				input::placeholder {
					color: rgb(211, 203, 203);
				}
			}
		}

		.submenu {
			width: 100%;
			margin: 0;
			background-color: var(--color);
			padding: 0px;
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;
			height: 34px;

			.scrollable::-webkit-scrollbar {
				display: none;
			}

			.button {
				height: 100%;
				display: flex;
				justify-content: center;
				align-items: center;
				background-color: #eee;
				padding: 2px;
				cursor: pointer;
				border-radius: 15px;
			}

			.scrollable-left {
				margin-right: 10px;
			}

			.scrollable-right {
				margin-left: 10px;
			}

			.scrollable {
				max-width: 750px;
				min-width: 500px;
				width: 80%;
				margin: 0;
				overflow: scroll;

				scrollbar-width: none;
				-ms-overflow-style: none;
				scroll-behavior: smooth;

				.items {
					width: max-content;
					display: flex;
					align-items: center;
					justify-content: flex-start;
					flex-wrap: nowrap;
					height: 100%;
					position: relative;

					a {
						color: #fff;
						padding: 5px;
						border-right: 1px solid #fff;
						font-weight: 100;
						font-size: 0.9em;
					}

					a:hover {
						color: rgb(236, 207, 207);
						transition: color 0.2s ease-in-out;
					}

					a.selected {
						background-color: #bd3030;
						color: #fff;
					}
				}
			}
		}
	}

	.menu {
		position: fixed;
		user-select: none;
		width: 70%;
		padding: 12px 12px 16px 12px;
		min-width: 220px;
		max-width: 350px;
		background-color: #fff;
		border: 1px solid #e8e2e2;
		border-top: none;
		border-radius: 0 0 12px 12px;
		box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
		z-index: 4;
		color: #000;
		overflow-y: auto;
		overflow-x: hidden;
		overscroll-behavior: contain;

		hr {
			width: 100%;
			margin: 0 auto 12px auto;
			border: none;
			border-top: 1px solid #eee;
		}

		a {
			display: block;
			margin: 0 0 6px 0;
			text-align: left;
			padding: 12px 48px 12px 14px;
			background-color: #faf7f7;
			border-radius: 0 8px 8px 0;
			border-left: 4px solid var(--border-color, #ed2724);
			transition:
				background-color 0.15s ease-in-out,
				color 0.15s ease-in-out;
		}

		.item {
			position: relative;

			.button {
				position: absolute;
				right: 10px;
				top: 5px;
				display: flex;
				background-color: rgba(0, 0, 0, 0.06);
				border-radius: 50%;
				cursor: pointer;
				padding: 9px;
				transition: background-color 0.15s ease-in-out;

				img {
					transition: transform 0.2s ease-in-out;
				}
			}

			.button:hover {
				background-color: rgba(0, 0, 0, 0.12);
			}
		}

		.childs-open {
			.button {
				img {
					transform: rotate(180deg);
				}
			}
		}

		.childs {
			padding-bottom: 4px;
		}

		a.child {
			margin-left: 24px;
			padding: 10px 14px;
			background-color: #fff;
			border-left-width: 3px;
		}

		a:hover {
			background-color: var(--border-color, #ed2724);
			color: #fff;
		}

		form.small {
			width: 100%;
			height: 38px;
			display: block;
			margin: 4px auto 14px auto;
			position: relative;

			input[type="search"] {
				position: absolute;
				width: 100%;
				height: 100%;
				left: 0px;
				top: 0px;
				text-align: center;
				outline: none;
				background-color: #fff;
				border: 1px solid #ccc;
				border-radius: 8px;
				color: #111;
				font-size: 16px;
				transition: border-color 0.15s ease-in-out;
			}

			input[type="search"]:focus {
				border-color: #ed2724;
			}

			input[type="search"]::-webkit-search-decoration,
			input[type="search"]::-webkit-search-cancel-button,
			input[type="search"]::-webkit-search-results-button,
			input[type="search"]::-webkit-search-results-decoration {
				display: none;
			}

			img {
				position: absolute;
				right: 7px;
				top: 7px;
				cursor: pointer;
			}
		}
	}

	.fake {
		height: 57px;
	}

	.fakeHidden {
		height: 0 !important;
	}

	.fakeWithSubmenu {
		height: 91px;
	}

	@media only screen and (max-width: 800px) {
		.fakeWithSubmenu {
			height: 57px;
		}
	}

	.menuShadow {
		position: fixed;
		left: 0px;
		top: 0px;
		width: 100%;
		height: 100%;
		z-index: 3;
		background-color: #111;
		opacity: 0.5;
	}

	.withShareContent {
		width: auto !important;
	}

	.withSubmenu {
		height: 91px;
	}

	@media only screen and (max-width: 800px) {
		.withSubmenu {
			height: auto;
		}

		.submenu {
			display: none !important;
		}

		.scrollable-left {
			display: none !important;
		}

		.scrollable-right {
			display: none !important;
		}

		.scrollable {
			display: none !important;
		}

		.topmenu {
			.subcontainer {
				justify-content: space-between;

				.buttons {
					margin-left: auto;
				}

				.share-btn {
					margin-left: auto;
					padding: 8px 14px;
				}

				.share-btn .share-label {
					display: inline;
				}
			}

			.title {
				display: none;
			}

			.category-tag {
				display: none;
			}
		}
	}
</style>
